// =============================================================================
//  Copyright   : 2018 semantics GmbH
//  Date        : $Date: 2026-02-15 16:17:55 +0100 (Sun, 15 Feb 2026) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 160719 $
//  Purpose     : templating sagas
// =============================================================================
// ---Third-party imports
import { select, put, take, race, delay } from 'redux-saga/effects';
import now from 'performance-now';
// ---Framework imports
import { get, items, size, dict, values, isObject, isString, isArray, isNil, filterItems } from 'ldash';
import { AuthError, NotfoundError, NetworkError, TimeoutError, CancelError } from 'uix/error';
import log from 'uix/logging';
import { prettyBytes, timeouts } from 'uix/utils';
import { toQueryString } from 'uix/utils/url';
import { normalize } from './normalize';
import { Actions, Sel } from 'uix/registries';
// ---Globals
// after normal http timeout, how long wait for websocket fallback
const WAIT_WS_TIMOUT_DATA = 180 // secs
;
export var cookieJar = null // expressjs
;
var browserCookies = null;
if (process.env.UIX_ENV == 'express') {
    let CookieJar = require('tough-cookie').CookieJar;
    cookieJar = new CookieJar();
    cookieJar.rejectPublicSuffixes = false;
} else {
    browserCookies = require('browser-cookies');
}
// static file helpers for breaking redux isolation in dev-server-mode
var _apiUrl = '';
export const hackApiUrl = (url)=>{
    _apiUrl = url;
};
export const GRAPHICS = (path)=>[
        _apiUrl,
        'static',
        'graphics',
        path
    ].join('/');
const buildUrl = (baseUrl, pathInfo)=>{
    let withBaseUrl = new RegExp('^https?://', 'i').test(pathInfo);
    return withBaseUrl ? pathInfo : baseUrl + pathInfo;
};
const logApiError = (error, url)=>{
    if ([
        'ECONNABORTED',
        'ECONNREFUSED'
    ].includes(error.code)) {
        // single line, no stack
        log.error('%s: %s on %o ', error.code, `${error}`.split(' at ')[0], url);
    } else {
        log.error('%o - %O on\n ', error.code, error.stack || error, url);
    }
};
const buildNonObjectResult = (data)=>({
        result: data
    });
const normalizeApiError = (error)=>{
    if (isObject(error)) {
        return error;
    }
    if (isString(error)) {
        return new Error(error);
    }
    if (isNil(error)) {
        return new Error('Unknown API error');
    }
    return new Error(String(error));
};
const isCancelError = (error, signal)=>Boolean(signal?.aborted) || error?.code == 'E_CANCEL';
const isLikelyNetworkError = (error)=>error?.code == 'E_NETWORK' || error?.name == 'TypeError' || error?.name == 'FetchError' || error?.cause?.code == 'ECONNREFUSED' || error?.cause?.code == 'ENOTFOUND' || error?.cause?.code == 'ECONNRESET';
const prepareAbortSignal = (incomingSignal, timeoutMs)=>{
    let controller = new AbortController();
    let timeoutId = null;
    let timedOut = false;
    let onAbort = null;
    let hasSignalApi = Boolean(incomingSignal?.addEventListener);
    if (hasSignalApi) {
        onAbort = ()=>controller.abort(incomingSignal.reason);
        if (incomingSignal.aborted) {
            onAbort();
        } else {
            incomingSignal.addEventListener('abort', onAbort);
        }
    }
    if (timeoutMs > 0) {
        timeoutId = setTimeout(()=>{
            timedOut = true;
            controller.abort(new Error('timeout'));
        }, timeoutMs);
    }
    let cleanup = ()=>{
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        if (hasSignalApi && onAbort) {
            incomingSignal.removeEventListener('abort', onAbort);
        }
    };
    return {
        signal: controller.signal,
        cleanup,
        wasTimedOut: ()=>timedOut
    };
};
const attachRequestCookies = (headers, jar, url)=>{
    if (!jar || process.env.UIX_ENV != 'express') {
        return headers;
    }
    let cookie = jar.getCookieStringSync(url);
    if (!cookie) {
        return headers;
    }
    return {
        ...headers,
        Cookie: cookie
    };
};
const persistResponseCookies = (response, jar, url)=>{
    if (!jar || process.env.UIX_ENV != 'express') {
        return;
    }
    let setCookies = [];
    if (response.headers.getSetCookie) {
        setCookies = response.headers.getSetCookie();
    } else {
        let cookie = response.headers.get('set-cookie');
        setCookies = cookie ? [
            cookie
        ] : [];
    }
    setCookies.forEach((cookie)=>jar.setCookieSync(cookie, url));
};
const parseResponseData = async (response)=>{
    let text = await response.text();
    if (text === '') {
        return {
            data: null,
            responseText: ''
        };
    }
    let contentType = (response.headers.get('content-type') || '').toLowerCase();
    let jsonCandidate = contentType.includes('application/json') || contentType.includes('+json') || text.startsWith('{') || text.startsWith('[');
    if (jsonCandidate) {
        try {
            return {
                data: JSON.parse(text),
                responseText: text
            };
        } catch  {
        // keep raw text to preserve previous behavior for invalid json payloads
        }
    }
    return {
        data: text,
        responseText: text
    };
};
const isBlobLike = (value)=>typeof Blob !== 'undefined' && value instanceof Blob || typeof File !== 'undefined' && value instanceof File;
const hasBinaryPayload = (params)=>Boolean(items(params || {}).find(([_, value])=>isArray(value) ? value.some((v)=>isBlobLike(v)) : isBlobLike(value)));
export const fetch = ({ apiurl, path, method, params, reqopts = {}, apiopts = {} })=>{
    // one log on-success below is enough
    //~ if (!apiopts.silent) log.debug(`API ${method}: ${apiurl}${path}`)
    method = method.toLowerCase();
    let ts = now();
    if (reqopts.timeout === Infinity) {
        reqopts = {
            ...reqopts,
            timeout: timeouts.MAX_TIMEOUT
        };
    } else if (!reqopts.timeout) {
        reqopts.timeout = timeouts.DEFAULT;
    }
    // log.api('reqopts: %o', reqopts)
    let options = {
        // XXX: withCredentials will send out session_id to remote server - use with care
        // do not use this for i3f requests to untrusted remote servers
        withCredentials: reqopts.withCredentials ?? true,
        ...reqopts
    };
    if (options.withHeaders ?? true) {
        // adding new headers need CORS support (for dev-mode) in vls/static/__init__.py
        let headers = {
            'VL-Client': 'uix',
            'VL-Language': 'de'
        };
        if (apiopts.apiCallUniqueId) {
            // for receiving data over websocket in timeout case
            headers = {
                ...headers,
                'VL-APICTRL': `${apiopts.apiCallUniqueId}|${options.timeout}`
            };
        }
        if (apiopts.balancerName) {
            headers = {
                ...headers,
                'Vl-Balance': apiopts.balancerName
            };
        }
        //~ log.x('%o %o', path, headers)
        options = {
            ...options,
            headers
        };
    }
    if (process.env.UIX_ENV == 'express') {
        options.jar = options.jar || cookieJar;
    // this needs to come from store / deploymode
    // and needs to be aligned with cookie data
    //~ apiurl = 'http://127.0.0.1'
    }
    let url = buildUrl(apiurl, path);
    if (method == 'get' && params && size(params) > 0) {
        url = `${url}?${toQueryString(params)}`;
    }
    let dataPayload = undefined;
    if (method == 'post') {
        let useFormData = process.env.UIX_ENV != 'express' && hasBinaryPayload(params);
        if (!useFormData) {
            options.headers = {
                ...options.headers,
                'Content-Type': 'application/x-www-form-urlencoded'
            };
            dataPayload = toQueryString(params);
        } else {
            dataPayload = new FormData();
            for (let [name, value] of items(params)){
                // this does also handle file streams
                if (isArray(value)) {
                    // should behave like parameter expansion in GET case
                    value.forEach((v)=>dataPayload.append(name, v));
                } else {
                    dataPayload.append(name, value);
                }
            }
        }
    }
    let jar = options.jar;
    let abortSignal = options.abortSignal;
    let requestHeaders = attachRequestCookies(options.headers || {}, jar, url);
    let fetchOptions = {
        method: method.toUpperCase(),
        headers: requestHeaders,
        body: method == 'post' ? dataPayload : undefined,
        credentials: options.withCredentials ? 'include' : 'omit'
    };
    let abortControl = prepareAbortSignal(abortSignal, options.timeout);
    fetchOptions.signal = abortControl.signal;
    return globalThis.fetch(url, fetchOptions).then(async (response)=>{
        persistResponseCookies(response, jar, url);
        let { data, responseText } = await parseResponseData(response);
        let status = response.status;
        let respSize = responseText ? prettyBytes(responseText.length) : '0 kB';
        if (!apiopts.silent) {
            log.api(`${url} [%o] [%s] [%o ms]`, status, respSize, parseInt(now() - ts));
        }
        if ((status < 200 || status >= 300) && status !== 401) {
            let requestError = new Error(`Request failed with status code ${status}`);
            requestError.response = {
                status
            };
            requestError.status = status;
            throw requestError;
        }
        if (!apiopts.raw) {
            // assume data is a json result, probably from /api/* or /api/filter/*
            if (isString(data) && data && data[0] == '{') {
                // json parsing errors give us also strings here
                // so we decided to reject bare string api results
                data = {
                    error: 'FetchError',
                    errorMsg: `bare string results are not allowed (json parsing error?)`,
                    url
                };
            }
            // convention: api results are always dicts
            // all other result types: null, scalars, lists are wrapped as "result"
            if (!isObject(data)) {
                data = buildNonObjectResult(data);
            }
            // there needs to be a explicit condition
            if (data.error) {
                // this is a cherrypy generated server error, wrapped in default json structure
                if (data.status == 401 || data.status == 403) {
                    return {
                        error: new AuthError(url, data.status, get(data, 'errorInfo.i18nReason', 'Auth Error'), get(data, 'errorMsg', get(data, 'error', ''))),
                        status: data.status
                    };
                } else if (data.status == 404) {
                    return {
                        error: new NotfoundError(url),
                        status: data.status
                    };
                } else if (data.status == 299) {
                    // websocket handled server side timeout (HandledTimeoutError)
                    return {
                        error: new TimeoutError(url, reqopts.timeout),
                        status: data.status
                    };
                } else {
                    // 500er in json msg
                    data.url = url;
                    status = data.status;
                }
            }
        } else {
            data = {
                responseText
            };
        }
        return {
            data,
            status
        };
    }).then((result)=>({
            ...result
        })).catch((error)=>{
        error = normalizeApiError(error);
        let status = 0;
        if (error.response) {
            status = error.response.status;
        }
        if (abortControl.wasTimedOut()) {
            return {
                error: new TimeoutError(url, reqopts.timeout),
                status
            };
        } else if (isCancelError(error, abortControl.signal)) {
            log.debug('request canceled: %o', error.message);
            // return neither error nor data
            return {
                error: new CancelError(),
                cancelled: true,
                status: null
            };
        } else if (status == 0 && isLikelyNetworkError(error)) {
            return {
                error: new NetworkError(url, error.message),
                status
            };
        } else {
            error.url = url;
            if (!error.code) error.code = 'NO_ERR_CODE';
            if (!apiopts.silentErrors) {
                logApiError(error, url);
            }
        }
        return {
            error,
            status
        };
    }).finally(()=>abortControl.cleanup());
};
let currentDomainSelector = (root)=>root.meta.request.dname;
let domainNameSelector = (odid)=>(root)=>root.config.domains.odid2name[odid];
let getDomainNames = (root)=>values(get(root, 'config.domains.odid2name', {}));
// our http GET and POST functions
function* getFetchArgs(path, apiopts) {
    let res = {
        apiurl: null,
        path
    };
    if (!path.startsWith('http')) {
        let apiurl = apiopts.apiurl;
        // pass through empty string
        if (apiurl === undefined || apiurl === null) {
            apiurl = yield select(Sel.apiurl);
        }
        res.apiurl = apiurl;
        let { autoDomain, odid, dname } = apiopts;
        if (!isNil(odid) || dname) {
            let domains = yield select((root)=>root.config.domains);
            autoDomain = false;
            if (!isNil(odid) && domains.odid2name[odid]) {
                dname = domains.odid2name[odid];
            }
            if (!domains.data[dname]) {
                throw Error(`invalid dname '${dname}'`);
            }
        }
        if (autoDomain) {
            let allDomainNames = yield select(getDomainNames);
            // check for a existing dname in path
            let candidate = path.split('/')[1];
            if (!allDomainNames.includes(candidate)) {
                dname = yield select(currentDomainSelector);
            }
        }
        if (dname) {
            if (path && path[0] == '/') {
                path = path.slice(1);
            }
            res.path = `/${dname}/${path}`;
        }
    }
    return res;
}
function* checkErrors(method, result) {
    /* transform client side and server side errors into uniform result
   */ let { error, data, cancelled } = result;
    if (error?.code == 'E_CANCEL') {
        // cancel case
        return {
            cancelled
        };
    } else if (error?.code == 'E_NETWORK') {
        // return nothing ?
        return {
            error
        };
    } else if (error) {
        let { message, stack, code, url } = error;
        // client side error
        yield put(Actions.registerApiError({
            message,
            stack,
            code,
            url
        }));
        return {
            error
        };
    } else if (data && data.error) {
        // server side error
        if (data.msg) {
            // normalize error message
            // errorMsg is the generic jsonp api wrapper form
            data.errorMsg = data.msg;
            delete data.msg;
        }
        yield put(Actions.registerServerError({
            pytype: data.error,
            message: data.errorMsg,
            stack: data.traceback,
            code: 'SERVER_ERROR',
            url: data.url
        }));
        log.warn('SERVER_ERROR: %o', data.errorMsg || '[no errorMsg]');
        return {
            error: {
                ...data,
                code: 'SERVER_ERROR'
            }
        };
    } else if (data) {
        return {
            data
        };
    } else {
        // no valid case
        return {};
    }
}
function* wait_for_timeout_data(res, apiCallUniqueId, path) {
    /* use websocket transfer for receiving timout data
  - send via server-side ApiTimeoutDataAction
  */ let { ws, online } = yield select((root)=>root.meta.netinfo);
    if (ws.state !== 'online' || !online) {
        log.warn('websocket or network offline: timout data not handled');
        return res;
    }
    // first check if we have already received the timeout data
    let apiTimeoutData = yield select((root)=>root.data.apiTimeoutData);
    let received_data = apiTimeoutData.filter((e)=>e.call_id == apiCallUniqueId)[0]?.api_data;
    if (!received_data) {
        // wait for timeout data
        let action_or_timeout = yield race({
            timeout_data: take((action)=>action.type == 'SERVER/API_TIMEOUT_DATA' && action.payload.call_id == apiCallUniqueId),
            timeout: delay(WAIT_WS_TIMOUT_DATA * 1000)
        });
        if (action_or_timeout.timeout) {
            log.warn('no timout data received after %o secs for %o (%o)', WAIT_WS_TIMOUT_DATA, path, apiCallUniqueId);
        } else {
            received_data = action_or_timeout.timeout_data.payload.api_data;
        //~ log.x('received data from action')
        }
    } else {
    //~ log.x('received data from store')
    }
    if (!received_data) {
        return res;
    } else {
        let data = null;
        // XXX: this looks ugly - why cant we decide str/object here?
        // /api/interpreter gives string results
        if (isString(received_data)) {
            try {
                data = JSON.parse(received_data);
            } catch  {
                log.warn('unparsable timeout data');
                data = buildNonObjectResult(received_data);
            }
        } else {
            data = received_data;
        }
        return {
            data,
            status: 299
        };
    }
}
function* get_post(method, path, params = {}, reqopts = {}, apiopts = {
    autoDomain: true
}) {
    // do not send undefineds over api
    params = dict(filterItems(params, (k, v)=>v !== undefined));
    let args = yield getFetchArgs(path, apiopts);
    let callStart = Actions.apiCallStart({
        method,
        path: args.path,
        params,
        reqopts,
        apiopts
    });
    let { apiCallId } = callStart.meta;
    yield put(callStart);
    let res = null;
    let status = null;
    try {
        let apiCallUniqueId = Date.now().toString(36) + Math.random().toString(36).substr(2);
        apiopts = {
            ...apiopts,
            apiCallUniqueId
        };
        res = yield fetch({
            ...args,
            params,
            method,
            reqopts,
            apiopts
        });
        // XXX: we dont know if the request has reached the server
        // so we dont know if the server can respond with a websocket api response transfer
        // ensure short running calls like auth/info do not wait if network is down
        let allowWait = !params.timeout || params.timeout >= timeouts.API_DEFAULT;
        if (res.error?.code === 'E_TIMEOUT' && allowWait) {
            res = yield wait_for_timeout_data(res, apiCallUniqueId, path);
        }
        status = res.status;
        if (!apiopts.raw) {
            res = yield checkErrors(method, res);
        }
    } finally{
        yield put(Actions.apiCallEnd({
            path: args.path,
            apiCallId,
            success: !res?.error,
            status,
            cancelled: Boolean(res?.cancelled)
        }));
    }
    return res;
}
let http = {
    /* return {data, error, loading} */ get: function*(...args) {
        return yield get_post('get', ...args);
    },
    post: function*(...args) {
        return yield get_post('post', ...args);
    }
};
function* initApp(pathname, cacheKeys) {
    // returns {data, error}
    let result = yield http.post('/api/init', {
        path_info: pathname,
        cacheKeys: JSON.stringify(cacheKeys)
    }, {
        timeout: timeouts.APP_INIT
    });
    // debug session behavior
    if (result.data && process.env.UIX_ENV.includes('browser')) {
        let sid = browserCookies.get('session_id');
        let session = get(result, 'data.user.session');
        if (sid != session) {
            let m = yield select((root)=>root.config.deploy.mode);
            if (m !== 'npm') {
                // expected in npm dev server mode
                log.warn('session mismatch browser %o != server-api %o [%o]', sid, session, m);
            }
        }
    }
    return result;
}
function* logout() {
    let { data, error } = yield http.get('/api/logout');
    let success = false, user = null;
    if (data) {
        success = true;
        user = data;
    }
    return {
        success,
        user
    };
}
let Api = {
    initApp,
    logout,
    get: http.get,
    post: http.post
};
export default Api;
